/**
 * 重要: このファイルはseedスクリプトからインポートされています
 * Prismaクライアントや外部依存を持つモジュールをインポートしないでください
 */

/**
 * 標準ロール名の定数
 * ロール名を参照する際はこの定数を使用してください
 */
export const STANDARD_ROLE_NAMES = {
  管理者: "管理者",
  人事労務担当者: "人事労務担当者",
  委託社会保険労務士: "委託社会保険労務士",
  産業保健スタッフ: "産業保健スタッフ",
  ストレスチェック実務担当者: "ストレスチェック実務担当者",
} as const;

/**
 * 標準ロール名の型
 */
export type StandardRoleName =
  (typeof STANDARD_ROLE_NAMES)[keyof typeof STANDARD_ROLE_NAMES];

/**
 * 事業所ユーザーの標準ロール
 * 事業所作成時に自動的に作成されるロール
 */
export const DEFAULT_EMPLOYER_ROLES = [
  {
    name: STANDARD_ROLE_NAMES.管理者,
    description:
      "全ての機能を利用できます。担当者の招待・削除やロールの変更、事業所情報の編集、権限設定の管理が可能です。",
  },
  {
    name: STANDARD_ROLE_NAMES.人事労務担当者,
    description:
      "被扶養者異動届、健診結果、認証コードなど日常業務に必要な機能を利用できます。担当者の管理や事業所情報の編集はできません。",
  },
  {
    name: STANDARD_ROLE_NAMES.委託社会保険労務士,
    description:
      "被扶養者異動届や申請情報の確認・操作が可能です。健診結果や認証コードの閲覧はできません。",
  },
  {
    name: STANDARD_ROLE_NAMES.産業保健スタッフ,
    description:
      "健診結果の確認・登録とダッシュボードの閲覧が可能です。届出や申請の操作はできません。",
  },
  {
    name: STANDARD_ROLE_NAMES.ストレスチェック実務担当者,
    description:
      "ストレスチェックの実施回の設定、対象者の登録、受検依頼の送信、集団分析の閲覧が可能です。個人の受検結果、事業所情報や他の担当者は閲覧できません。",
  },
] as const;

/**
 * 事業所ユーザーのアクション/リソース組み合わせ定義
 */
export const EMPLOYER_ABILITY_DEFINITION = {
  Employer: ["invite", "remove", "changeRole", "read"],
  Office: ["update", "read"],
  MedicalCheckup: ["read", "update"],
  MedicalCheckupSubsidyNotice: ["read"],
  Dashboard: ["read"],
  Offer: ["read"],
  Application: ["read"],
  HihuyousyaIdouTodoke: ["read", "update"],
  Hifuyoushashikakukakunin: ["read", "update"],
  Fuufukyoudoufuyoukakunin: ["read", "update"],
  Shussanteatekin: ["read", "update"],
  Member: ["read"],
  MemberAuthCode: ["read", "update"],
  Permission: ["read", "update"],
  StressCheck: ["read", "update"],
} as const;

/**
 * 事業所ユーザーの権限 1 つ（employer_abilities の resource / action の組）。
 * EMPLOYER_ABILITY_DEFINITION にある組み合わせだけを受け付ける
 */
export type EmployerAbilityKey = {
  [R in keyof typeof EMPLOYER_ABILITY_DEFINITION]: {
    resource: R;
    action: (typeof EMPLOYER_ABILITY_DEFINITION)[R][number];
  };
}[keyof typeof EMPLOYER_ABILITY_DEFINITION];

/**
 * 管理者ロールのみに付与される権限（カスタムロールには付与不可）。
 * 外から使うのは CUSTOM_ROLE_FORBIDDEN_PERMISSIONS なので公開しない
 */
const ADMIN_ONLY_PERMISSIONS: ReadonlyArray<{
  resource: string;
  action: string;
}> = [
  { resource: "Employer", action: "invite" },
  { resource: "Employer", action: "remove" },
  { resource: "Employer", action: "changeRole" },
  { resource: "Permission", action: "read" },
  { resource: "Permission", action: "update" },
];

/**
 * ストレスチェックの権限。
 * 専用のプリセットロール（ストレスチェック実務担当者）にのみ付与する。
 * ストレスチェックは機微情報を扱うため、他のプリセットロールには既定で付与しない。
 * 外から使うのは CUSTOM_ROLE_FORBIDDEN_PERMISSIONS なので公開しない
 */
const STRESS_CHECK_PERMISSIONS: ReadonlyArray<{
  resource: string;
  action: string;
}> = [
  { resource: "StressCheck", action: "read" },
  { resource: "StressCheck", action: "update" },
];

/**
 * カスタムロールに付与できない権限。
 * 管理者ロール専用の権限と、ストレスチェックの権限（カスタムロールへ付与できるようにするかが
 * 未決のため今は弾く）をまとめる
 */
export const CUSTOM_ROLE_FORBIDDEN_PERMISSIONS: ReadonlyArray<{
  resource: string;
  action: string;
}> = [...ADMIN_ONLY_PERMISSIONS, ...STRESS_CHECK_PERMISSIONS];

/**
 * 標準ロールの権限マッピング
 */
export const DEFAULT_EMPLOYER_ROLE_ABILITIES: Record<
  StandardRoleName,
  ReadonlyArray<{
    resource: keyof typeof EMPLOYER_ABILITY_DEFINITION;
    action: (typeof EMPLOYER_ABILITY_DEFINITION)[keyof typeof EMPLOYER_ABILITY_DEFINITION][number];
  }>
> = {
  [STANDARD_ROLE_NAMES.管理者]: [
    { resource: "Employer", action: "invite" },
    { resource: "Employer", action: "remove" },
    { resource: "Employer", action: "changeRole" },
    { resource: "Employer", action: "read" },
    { resource: "Office", action: "update" },
    { resource: "Office", action: "read" },
    { resource: "MedicalCheckup", action: "read" },
    { resource: "MedicalCheckup", action: "update" },
    { resource: "MedicalCheckupSubsidyNotice", action: "read" },
    { resource: "Dashboard", action: "read" },
    { resource: "Offer", action: "read" },
    { resource: "Application", action: "read" },
    { resource: "HihuyousyaIdouTodoke", action: "read" },
    { resource: "HihuyousyaIdouTodoke", action: "update" },
    { resource: "Hifuyoushashikakukakunin", action: "read" },
    { resource: "Hifuyoushashikakukakunin", action: "update" },
    { resource: "Fuufukyoudoufuyoukakunin", action: "read" },
    { resource: "Fuufukyoudoufuyoukakunin", action: "update" },
    { resource: "Shussanteatekin", action: "read" },
    { resource: "Shussanteatekin", action: "update" },
    { resource: "Member", action: "read" },
    { resource: "MemberAuthCode", action: "read" },
    { resource: "MemberAuthCode", action: "update" },
    { resource: "Permission", action: "read" },
    { resource: "Permission", action: "update" },
  ],
  [STANDARD_ROLE_NAMES.人事労務担当者]: [
    { resource: "Employer", action: "read" },
    { resource: "Office", action: "read" },
    { resource: "MedicalCheckup", action: "read" },
    { resource: "MedicalCheckup", action: "update" },
    { resource: "MedicalCheckupSubsidyNotice", action: "read" },
    { resource: "Dashboard", action: "read" },
    { resource: "Offer", action: "read" },
    { resource: "Application", action: "read" },
    { resource: "HihuyousyaIdouTodoke", action: "read" },
    { resource: "HihuyousyaIdouTodoke", action: "update" },
    { resource: "Hifuyoushashikakukakunin", action: "read" },
    { resource: "Hifuyoushashikakukakunin", action: "update" },
    { resource: "Fuufukyoudoufuyoukakunin", action: "read" },
    { resource: "Fuufukyoudoufuyoukakunin", action: "update" },
    { resource: "Shussanteatekin", action: "read" },
    { resource: "Shussanteatekin", action: "update" },
    { resource: "Member", action: "read" },
    { resource: "MemberAuthCode", action: "read" },
    { resource: "MemberAuthCode", action: "update" },
  ],
  [STANDARD_ROLE_NAMES.委託社会保険労務士]: [
    { resource: "Employer", action: "read" },
    { resource: "Office", action: "read" },
    { resource: "Application", action: "read" },
    { resource: "HihuyousyaIdouTodoke", action: "read" },
    { resource: "HihuyousyaIdouTodoke", action: "update" },
    { resource: "Hifuyoushashikakukakunin", action: "read" },
    { resource: "Hifuyoushashikakukakunin", action: "update" },
    { resource: "Fuufukyoudoufuyoukakunin", action: "read" },
    { resource: "Fuufukyoudoufuyoukakunin", action: "update" },
    { resource: "Shussanteatekin", action: "read" },
    { resource: "Shussanteatekin", action: "update" },
    { resource: "Member", action: "read" },
  ],
  [STANDARD_ROLE_NAMES.産業保健スタッフ]: [
    { resource: "Employer", action: "read" },
    { resource: "Office", action: "read" },
    { resource: "MedicalCheckup", action: "read" },
    { resource: "MedicalCheckup", action: "update" },
    { resource: "MedicalCheckupSubsidyNotice", action: "read" },
    { resource: "Dashboard", action: "read" },
    { resource: "Member", action: "read" },
  ],
  // ストレスチェックだけを担う実務担当者。他のプリセットロールと違い、
  // 事業所情報・担当者の閲覧も持たせない（ストレスチェックの担当者が
  // 他の業務の情報まで見られる必要はない）
  [STANDARD_ROLE_NAMES.ストレスチェック実務担当者]: [
    { resource: "StressCheck", action: "read" },
    { resource: "StressCheck", action: "update" },
  ],
};
